// Session 147j+4 — ValueHunter PIT fundamentals coverage card.
// Shows: backfill status + universe coverage + history depth + manual
// trigger button. Visible only on the ValueHunter profile.

import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Database, RefreshCw, Activity, CheckCircle2, AlertCircle, Clock } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = 'token';
const authHeaders = () => {
  const tok = localStorage.getItem(TOKEN_KEY);
  return tok ? { Authorization: `Bearer ${tok}` } : {};
};

const formatTimeAgo = (iso) => {
  if (!iso) return 'never';
  const t = new Date(iso).getTime();
  const diff = Math.floor((Date.now() - t) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};

const PITCoverageCard = ({ profile = 'valuehunter', readOnly = false }) => {
  const [coverage, setCoverage] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (readOnly) return;   // admin-only card — skip fetch for regular users
    try {
      const r = await axios.get(
        `${API}/api/admin/valuehunter/coverage?profile=${profile}`,
        { headers: authHeaders() },
      );
      setCoverage(r.data);
    } catch (e) {
      // silent fail — widget is optional
      console.warn('[VH PIT] coverage load failed', e?.response?.status);
    }
  }, [profile, readOnly]);

  useEffect(() => {
    if (readOnly) return;
    load();
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, [load, readOnly]);

  const trigger = async () => {
    setBusy(true);
    try {
      await axios.post(
        `${API}/api/admin/valuehunter/backfill-fundamentals?profile=${profile}&years=6&only_missing=true`,
        {},
        { headers: authHeaders() },
      );
      toast.success('PIT backfill triggered. Will run in background.');
      await load();
    } catch (e) {
      toast.error(`Trigger failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setBusy(false);
    }
  };

  if (!coverage) {
    return null;
  }

  const cov = coverage.coverage_pct || 0;
  const state = coverage.backfill_state || {};
  const isRunning = !!state.is_running;
  const buckets = coverage.history_buckets || {};
  const fiveY = buckets['≥5y_history'] || 0;
  const threeY = buckets['≥3y_history'] || 0;
  const oneY = buckets['≥1y_history'] || 0;

  // Tone: green if cov ≥ 80%, amber 40-80%, rose < 40%
  const tone = cov >= 80
    ? { border: 'border-emerald-500/40', bg: 'bg-emerald-500/5', text: 'text-emerald-300', icon: CheckCircle2 }
    : cov >= 40
      ? { border: 'border-amber-500/40', bg: 'bg-amber-500/5', text: 'text-amber-300', icon: AlertCircle }
      : { border: 'border-rose-500/40', bg: 'bg-rose-500/5', text: 'text-rose-300', icon: AlertCircle };

  const Icon = tone.icon;

  return (
    <div
      className={`border ${tone.border} ${tone.bg} p-3 flex items-center gap-4 flex-wrap`}
      data-testid="vh-pit-coverage-card"
    >
      <div className="flex items-center gap-2">
        <Database className={`w-4 h-4 ${tone.text}`} />
        <span className="text-[11px] font-mono uppercase tracking-wider text-foreground">
          PIT Fundamentals
        </span>
      </div>
      <div className="flex items-center gap-1.5">
        <Icon className={`w-3 h-3 ${tone.text}`} />
        <span className={`text-xs font-mono ${tone.text}`} data-testid="vh-pit-coverage-pct">
          {coverage.covered_symbols}/{coverage.universe_size}
        </span>
        <span className="text-[10px] font-mono text-muted-foreground">
          ({cov.toFixed(1)}%)
        </span>
      </div>
      <div className="flex items-center gap-2 text-[10px] font-mono text-muted-foreground">
        <span className="px-1.5 py-0.5 border border-border rounded">≥1y: <span className="text-foreground">{oneY}</span></span>
        <span className="px-1.5 py-0.5 border border-border rounded">≥3y: <span className="text-foreground">{threeY}</span></span>
        <span className="px-1.5 py-0.5 border border-border rounded">≥5y: <span className="text-foreground">{fiveY}</span></span>
      </div>
      {state.finished_at && !isRunning && (
        <div className="flex items-center gap-1 text-[10px] font-mono text-muted-foreground">
          <Clock className="w-3 h-3" />
          last backfill: {formatTimeAgo(state.finished_at)}
          {' · '}{state.rows_persisted || 0} rows
        </div>
      )}
      {isRunning && (
        <div className="flex items-center gap-1.5 text-[10px] font-mono text-violet-300">
          <Activity className="w-3 h-3 animate-pulse" />
          Backfilling: {state.processed || 0}/{state.total || 0}
          {' · '}{state.rows_persisted || 0} rows
        </div>
      )}
      <div className="ml-auto">
        <button
          onClick={trigger}
          disabled={busy || isRunning}
          className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-mono uppercase tracking-wider border border-border hover:border-violet-500 hover:text-violet-300 disabled:opacity-50"
          data-testid="vh-pit-backfill-btn"
        >
          <RefreshCw className={`w-3 h-3 ${busy ? 'animate-spin' : ''}`} />
          {isRunning ? 'Running…' : 'Refresh PIT data'}
        </button>
      </div>
    </div>
  );
};

export default PITCoverageCard;
